import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del } from "../client";
import type {
  Work,
  WorkSummary,
  Expression,
  ExpressionListItem,
  CreateWorkInput,
  UpdateWorkInput,
  CreateExpressionInput,
  UpdateExpressionInput,
} from "../types";

const WORKS_KEY = "catalog-works";
const EXPRESSIONS_KEY = "catalog-expressions";

// ============================================================
// Work hooks
// ============================================================

export interface WorkSearchParams {
  q?: string | undefined;
  form?: string | undefined;
  page?: number | undefined;
  limit?: number | undefined;
}

/**
 * Search works.
 */
export function useWorksSearch(params: WorkSearchParams = {}) {
  const queryParams = new URLSearchParams();

  if (params.q) queryParams.set("q", params.q);
  if (params.form) queryParams.set("form", params.form);
  if (params.page) queryParams.set("page", String(params.page));
  if (params.limit) queryParams.set("limit", String(params.limit));

  const queryString = queryParams.toString();

  return useQuery({
    queryKey: [WORKS_KEY, "search", params],
    queryFn: async () => {
      const url = queryString ? `/catalog/works?${queryString}` : "/catalog/works";
      return get<WorkSummary[]>(url);
    },
  });
}

/**
 * Get a single work by ID.
 */
export function useWork(id: string | undefined) {
  return useQuery({
    queryKey: [WORKS_KEY, id],
    queryFn: async () => {
      if (!id) throw new Error("ID is required");
      return get<Work>(`/catalog/works/${id}`);
    },
    enabled: !!id,
  });
}

/**
 * Create a new work.
 */
export function useCreateWork() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateWorkInput) => {
      return post<Work>("/catalog/works", input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [WORKS_KEY] });
    },
  });
}

/**
 * Update an existing work.
 */
export function useUpdateWork(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateWorkInput) => {
      return patch<Work>(`/catalog/works/${id}`, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [WORKS_KEY] });
    },
  });
}

/**
 * Delete a work.
 */
export function useDeleteWork() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await del(`/catalog/works/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [WORKS_KEY] });
    },
  });
}

// ============================================================
// Expression hooks
// ============================================================

export interface ExpressionSearchParams {
  workId?: string;
  language?: string;
  page?: number;
  limit?: number;
}

/**
 * Search expressions.
 */
export function useExpressionsSearch(params: ExpressionSearchParams = {}) {
  const queryParams = new URLSearchParams();

  if (params.language) queryParams.set("language", params.language);
  if (params.page) queryParams.set("page", String(params.page));
  if (params.limit) queryParams.set("limit", String(params.limit));

  const queryString = queryParams.toString();

  // Use the work-scoped endpoint if workId is provided
  const baseUrl = params.workId
    ? `/catalog/works/${params.workId}/expressions`
    : "/catalog/expressions";

  return useQuery({
    queryKey: [EXPRESSIONS_KEY, "search", params],
    queryFn: async () => {
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;
      return get<ExpressionListItem[]>(url);
    },
  });
}

/**
 * Get expressions for a work.
 */
export function useWorkExpressions(workId: string | undefined) {
  return useQuery({
    queryKey: [EXPRESSIONS_KEY, "work", workId],
    queryFn: async () => {
      if (!workId) throw new Error("Work ID is required");
      return get<ExpressionListItem[]>(`/catalog/works/${workId}/expressions`);
    },
    enabled: !!workId,
  });
}

/**
 * Get a single expression by ID.
 */
export function useExpression(id: string | undefined) {
  return useQuery({
    queryKey: [EXPRESSIONS_KEY, id],
    queryFn: async () => {
      if (!id) throw new Error("ID is required");
      return get<Expression>(`/catalog/expressions/${id}`);
    },
    enabled: !!id,
  });
}

/**
 * Create a new expression.
 */
export function useCreateExpression() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateExpressionInput) => {
      return post<Expression>("/catalog/expressions", input);
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: [EXPRESSIONS_KEY] });
      // Also invalidate the work since expression count changed
      void queryClient.invalidateQueries({ queryKey: [WORKS_KEY, data.workId] });
    },
  });
}

/**
 * Update an existing expression.
 */
export function useUpdateExpression(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateExpressionInput) => {
      return patch<Expression>(`/catalog/expressions/${id}`, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [EXPRESSIONS_KEY] });
    },
  });
}

/**
 * Delete an expression.
 */
export function useDeleteExpression() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await del(`/catalog/expressions/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [EXPRESSIONS_KEY] });
      void queryClient.invalidateQueries({ queryKey: [WORKS_KEY] });
    },
  });
}
